import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { pdfPreviewProvider } from '../../providers/pdfPreviewProvider';
import type { PdfPreviewMedia as Media, PdfPreviewUnit } from '../../types/pdfPreview';
import { MyButton } from '../uiComponents';
import { pdfFilledButtonSx } from './pdfImportButtonStyles';

const PAGE_SIZE = 12;
const MAX_CACHE = 24;
const MAX_CONCURRENT = 4;

type Props = {
  previewId: string;
  media: Media[];
  unit: PdfPreviewUnit;
  selectedMediaIds: string[];
  disabled: boolean;
  onToggle: (mediaId: string) => void;
  onMove: (mediaId: string, direction: -1 | 1) => void;
  onExpired: () => void;
};

export const PdfPreviewMedia = ({
  previewId,
  media,
  unit,
  selectedMediaIds,
  disabled,
  onToggle,
  onMove,
  onExpired,
}: Props) => {
  const [filter, setFilter] = useState<'all' | 'suggested' | 'selected'>('all');
  const [page, setPage] = useState(0);
  const [, forceRender] = useState(0);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const cache = useRef(new Map<string, string>());
  const controllers = useRef(new Map<string, AbortController>());
  const queued = useRef<string[]>([]);
  const activeCount = useRef(0);
  const alive = useRef(true);
  const wanted = useRef(new Set<string>());
  const failed = useRef(new Set<string>());
  const mediaById = useRef(new Map(media.map((item) => [item.mediaId, item])));
  mediaById.current = new Map(media.map((item) => [item.mediaId, item]));

  const visibleMedia =
    filter === 'selected'
      ? selectedMediaIds
          .map((id) => mediaById.current.get(id))
          .filter((item): item is Media => Boolean(item))
      : media.filter(
          (item) =>
            filter === 'all' ||
            Boolean(
              unit.recommendedMediaIds?.includes(item.mediaId) ||
                unit.reviewMediaIds?.includes(item.mediaId)
            )
        );
  const pageCount = Math.max(1, Math.ceil(visibleMedia.length / PAGE_SIZE));
  const effectivePage = Math.min(page, pageCount - 1);
  const pageMedia = visibleMedia.slice(effectivePage * PAGE_SIZE, (effectivePage + 1) * PAGE_SIZE);

  const pump = useCallback(() => {
    while (
      alive.current &&
      !disabled &&
      activeCount.current < MAX_CONCURRENT &&
      queued.current.length
    ) {
      const mediaId = queued.current.shift()!;
      if (
        !wanted.current.has(mediaId) ||
        cache.current.has(mediaId) ||
        controllers.current.has(mediaId) ||
        failed.current.has(mediaId)
      )
        continue;
      const item = mediaById.current.get(mediaId);
      if (!item || item.status !== 'prepared') continue;
      const controller = new AbortController();
      controllers.current.set(mediaId, controller);
      activeCount.current += 1;
      void pdfPreviewProvider
        .getMedia(previewId, mediaId, controller.signal)
        .then((response) => {
          if (!alive.current || controller.signal.aborted) return;
          if (!response.ok || !(response.data instanceof Blob)) {
            if (response.status === 410) onExpired();
            failed.current.add(mediaId);
            setErrors((old) => ({ ...old, [mediaId]: true }));
            return;
          }
          const url = URL.createObjectURL(response.data);
          cache.current.set(mediaId, url);
          while (cache.current.size > MAX_CACHE) {
            const removeId = Array.from(cache.current.keys()).find((id) => !wanted.current.has(id));
            if (!removeId) break;
            URL.revokeObjectURL(cache.current.get(removeId)!);
            cache.current.delete(removeId);
          }
          forceRender((old) => old + 1);
        })
        .catch(() => {
          if (alive.current && !controller.signal.aborted) {
            failed.current.add(mediaId);
            setErrors((old) => ({ ...old, [mediaId]: true }));
          }
        })
        .finally(() => {
          controllers.current.delete(mediaId);
          activeCount.current -= 1;
          pump();
        });
    }
  }, [disabled, onExpired, previewId]);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      queued.current = [];
      controllers.current.forEach((controller) => controller.abort());
      controllers.current.clear();
      cache.current.forEach((url) => URL.revokeObjectURL(url));
      cache.current.clear();
    };
  }, [previewId]);

  useEffect(() => {
    wanted.current = new Set(pageMedia.map((item) => item.mediaId));
    queued.current = pageMedia
      .filter(
        (item) =>
          item.status === 'prepared' &&
          !cache.current.has(item.mediaId) &&
          !failed.current.has(item.mediaId)
      )
      .map((item) => item.mediaId);
    pump();
  }, [effectivePage, filter, unit.unitKey, media, selectedMediaIds, disabled, pump]);

  const button = (label: string, onClick: () => void, unavailable = false) => (
    <MyButton
      data={{
        buttonName: label,
        buttonType: 'button',
        variant: 'contained',
        sx: pdfFilledButtonSx,
      }}
      disabled={unavailable || disabled}
      onClick={onClick}
    />
  );

  return (
    <Stack spacing={2}>
      <Typography variant='body2'>
        Selected for this listing: {selectedMediaIds.length} / 50. Suggestions need your review;
        unassigned images remain available.
      </Typography>
      <TextField
        label='Media filter'
        select
        value={filter}
        onChange={(event) => {
          setFilter(event.target.value as typeof filter);
          setPage(0);
        }}
        sx={{ maxWidth: 240 }}
      >
        <MenuItem value='all'>All media</MenuItem>
        <MenuItem value='suggested'>Suggested for this listing</MenuItem>
        <MenuItem value='selected'>Selected for this listing</MenuItem>
      </TextField>
      <Typography variant='body2'>
        Showing {visibleMedia.length} images. Page {effectivePage + 1} of {pageCount}.
      </Typography>
      <Stack direction='row' flexWrap='wrap' gap={2}>
        {pageMedia.map((item) => {
          const selectedIndex = selectedMediaIds.indexOf(item.mediaId);
          const selectable =
            item.status === 'prepared' && !unit.excludedMediaIds?.includes(item.mediaId);
          return (
            <Paper
              key={item.mediaId}
              sx={{
                border: selectedIndex >= 0 ? '2px solid' : '1px solid',
                borderColor: selectedIndex >= 0 ? 'primary.main' : 'divider',
                p: 1.5,
                width: { xs: '100%', sm: 210 },
              }}
            >
              <Stack spacing={1}>
                <Box
                  alignItems='center'
                  display='flex'
                  justifyContent='center'
                  sx={{ backgroundColor: 'action.hover', height: 145, overflow: 'hidden' }}
                >
                  {cache.current.has(item.mediaId) && !errors[item.mediaId] ? (
                    <img
                      alt={`${item.kind} from PDF page ${item.occurrences[0]?.pageNumber ?? '?'}`}
                      loading='lazy'
                      onError={() => setErrors((old) => ({ ...old, [item.mediaId]: true }))}
                      src={cache.current.get(item.mediaId)}
                      style={{ maxHeight: '100%', maxWidth: '100%' }}
                    />
                  ) : errors[item.mediaId] ? (
                    <Typography role='alert' variant='caption'>
                      Image unavailable
                    </Typography>
                  ) : item.status !== 'prepared' ? (
                    <Typography variant='caption'>Unavailable</Typography>
                  ) : (
                    <CircularProgress aria-label='Loading image' size={24} />
                  )}
                </Box>
                <Typography variant='body2'>
                  {item.kind.replace(/_/g, ' ')} · {item.association.scope}
                </Typography>
                <Typography variant='caption'>
                  Page {item.occurrences[0]?.pageNumber ?? '?'}
                  {item.needsReview ? ' · Review required' : ''}
                  {selectedIndex >= 0 ? ` · Selected order: ${selectedIndex + 1}` : ''}
                </Typography>
                {item.reviewReasons.map((reason) => (
                  <Typography key={reason} color='warning.main' variant='caption'>
                    {reason}
                  </Typography>
                ))}
                {button(
                  selectedIndex >= 0 ? 'Remove' : 'Select',
                  () => onToggle(item.mediaId),
                  !selectable || (selectedIndex < 0 && selectedMediaIds.length >= 50)
                )}
                {selectedIndex >= 0 && (
                  <Stack direction='row' gap={1}>
                    {button('Up', () => onMove(item.mediaId, -1), selectedIndex === 0)}
                    {button(
                      'Down',
                      () => onMove(item.mediaId, 1),
                      selectedIndex === selectedMediaIds.length - 1
                    )}
                  </Stack>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      {visibleMedia.length === 0 && <Typography>No images in this filter.</Typography>}
      <Stack direction='row' gap={2}>
        {button('Previous', () => setPage(Math.max(0, effectivePage - 1)), effectivePage === 0)}
        {button(
          'Next',
          () => setPage(Math.min(pageCount - 1, effectivePage + 1)),
          effectivePage + 1 >= pageCount
        )}
      </Stack>
    </Stack>
  );
};
